<script setup lang="ts">
import '@/utils/floorUtils.ts';

import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import XMarkSolid from '@/components/svg/XMarkSolid.vue';
import { useFloor } from '@/composables/useFloor';
import { useUnit } from '@/composables/useUnit';
import { UnitsFilterType } from '@/enums/UnitsFilterType';
import { formatCurrencyWithoutDecimal } from '@/helpers/currencyHelper.ts';
import type { LocaleSchema } from '@/locales/index';
import type { UnitsAppliedFilter } from '@/typings/units/UnitsAppliedFilters';
import { type UnitsFilters } from '@/typings/units/UnitsFilters';

const filters = defineModel<UnitsFilters>('filters');

const props = defineProps<{
  allowFilterDeletion?: boolean;
}>();

const { t, locale } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});

const { getFloorTextByNumber } = useFloor();
const { getStatusText } = useUnit();

const appliedFilters = computed(() => {
  if (!filters.value) {
    return [];
  }

  const appliedFilters = [] as UnitsAppliedFilter[];

  if (filters.value.selectedFloorNo) {
    appliedFilters.push({
      filterType: UnitsFilterType.FloorSelection,
      value: filters.value.selectedFloorNo,
      displayValue: getFloorTextByNumber(filters.value.selectedFloorNo),
    });
  }
  if (filters.value.selectedPriceRange) {
    appliedFilters.push({
      filterType: UnitsFilterType.UnitPrice,
      value: filters.value.selectedPriceRange,
      displayValue: t('forms.units.filters.applied.unitPrice', { min: formatCurrencyWithoutDecimal(filters.value.selectedPriceRange.min ?? 0, locale), max: formatCurrencyWithoutDecimal(filters.value.selectedPriceRange.max ?? 0, locale) }),
    });
  }
  if (filters.value.selectedLivingAreaRange) {
    appliedFilters.push({
      filterType: UnitsFilterType.LivingArea,
      value: filters.value.selectedLivingAreaRange,
      displayValue: t('forms.units.filters.applied.livingArea', { min: filters.value.selectedLivingAreaRange.min, max: filters.value.selectedLivingAreaRange.max }),
    });
  }
  if (filters.value.selectedBedroomsCounts) {
    for (const count of filters.value.selectedBedroomsCounts) {
      appliedFilters.push({
        filterType: UnitsFilterType.BedroomsCount,
        value: count,
        displayValue: t('forms.units.filters.applied.bedroomsCount', count),
      });
    }
  }
  if (filters.value.selectedStatuses) {
    for (const status of filters.value.selectedStatuses) {
      const statusText = getStatusText(status);
      if (statusText) {
        appliedFilters.push({
          filterType: UnitsFilterType.Status,
          value: status,
          displayValue: statusText,
        });
      }
    }
  }

  return appliedFilters;
});

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length > 0;
});

const onDeleteFilter = (filter: UnitsAppliedFilter) => {
  if(!filters.value) {
    return;
  }
  
  switch (filter.filterType) {
  case UnitsFilterType.FloorSelection:
    filters.value.selectedFloorNo = undefined;
    break;
  case UnitsFilterType.UnitPrice:
    filters.value.selectedPriceRange = undefined;
    break;
  case UnitsFilterType.LivingArea:
    filters.value.selectedLivingAreaRange = undefined;
    break;
  case UnitsFilterType.BedroomsCount:
    filters.value.selectedBedroomsCounts = filters.value.selectedBedroomsCounts?.filter((count) => count !== filter.value) ?? [];
    break;
  case UnitsFilterType.Status:
    filters.value.selectedStatuses = filters.value.selectedStatuses?.filter((status) => status !== filter.value) ?? [];
    break;
  }
};
</script>

<template>
  <div class="appliedFilters">
    <menu v-if="hasAppliedFilters"
          class="appliedFilters-list">
      <li v-for="filter in appliedFilters"
          :key="filter.filterType"
          class="appliedFilters-item">
        <button v-if="props.allowFilterDeletion"
                class="appliedFilters-deleteButton btn"
                @click="onDeleteFilter(filter)">
          <XMarkSolid class="appliedFilters-deleteIcon" />
          <span>{{ filter.displayValue }}</span>
        </button>
      </li>
    </menu>
  </div>
</template>

<style lang="scss">
.appliedFilters {
  &-list {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--buttonGap, 8px);
  }

  &-deleteButton {
    --buttonBackgroundColor: var(--gray-900);
    
    border-radius: 32px;

    color: var(--white);
  }
}
</style>
